import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Checkbox } from 'common-src/components/base';
import Button from 'common-src/components/base/Button/Button';
import { apiRequest, getRestErrorMessage, HttpMethods } from 'common-src/features/rest';

import useAlert from 'src/hooks/useAlert';
import BasePopup from 'src/popups/BasePopup';

import styles from './ImportXifinCoveragePopup.module.scss';

const UPLOAD_URL_ENDPOINT = 'xifinCoverageImports/uploadUrl';
const PREVIEW_ENDPOINT = 'xifinCoverageImports/preview';
const LATEST_PREVIEW_ENDPOINT = 'xifinCoverageImports/latestPreview';
const stateEndpoint = (importId) => `xifinCoverageImports/${importId}`;
const applyEndpoint = (importId) => `xifinCoverageImports/${importId}/apply`;

const POLL_MS = 3000;

const FILE_ERROR = 'Please select a .csv file.';
const DEFAULT_ERROR = 'Unable to process the XIFIN coverage file. Please try again.';

const EXPECTED_COLUMNS = [
  'xifin_patient_id',
  'insurance_sequence',
  'payer_code',
  'payer_name',
  'member_id',
  'subscriber_relationship',
  'event_timestamp',
];

const COUNT_ROWS = [
  { key: 'patientsProcessed', label: 'Patients processed' },
  { key: 'wouldReplace', label: 'Would be replaced' },
  { key: 'replaced', label: 'Replaced' },
  { key: 'unchanged', label: 'Unchanged' },
  { key: 'needsReview', label: 'Needs review' },
  { key: 'notFound', label: 'MRN not found' },
  { key: 'failed', label: 'Failed' },
  { key: 'createdCoverages', label: 'Coverages created' },
  { key: 'expiredCoverages', label: 'Coverages expired' },
  { key: 'createdExemptions', label: 'Exemptions created' },
  { key: 'expiredExemptions', label: 'Exemptions expired' },
  { key: 'preauthsRepointed', label: 'Pre-auths re-pointed' },
  { key: 'preauthsOrphaned', label: 'Pre-auths orphaned' },
];

const RUNNING_STATUSES = new Set(['previewing', 'processing']);

const isCsvFile = (file) => {
  const name = file?.name?.toLowerCase() || '';
  return file?.type === 'text/csv' || name.endsWith('.csv');
};

const outcomeClass = (outcome) => {
  if (outcome === 'replaced' || outcome === 'would_replace') return styles.outcomeChange;
  if (outcome === 'unchanged') return styles.outcomeOk;
  return styles.outcomeReview;
};

const summaryLine = (row) => {
  const before = (row.before?.coverages || []).join(', ') || '—';
  const after = (row.after?.coverages || []).join(', ') || '—';
  return `${before}  →  ${after}`;
};

const parseMrnList = (raw) =>
  raw
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter(Boolean);

const scopedCsv = async (file, mrnList) => {
  if (!mrnList.length) return file;
  const wanted = new Set(mrnList);
  const kept = (await file.text()).split(/\r?\n/).filter((line, index) => {
    if (index === 0) return true;
    if (!line.trim()) return false;
    return wanted.has(line.slice(0, line.indexOf(',')).trim());
  });
  return new File([`${kept.join('\n')}\n`], file.name, { type: 'text/csv' });
};

const uploadToS3 = async (csv) => {
  const res = await apiRequest({
    endpoint: UPLOAD_URL_ENDPOINT,
    method: HttpMethods.Post,
    body: { filename: csv.name },
  });
  if (!res.ok) throw new Error("Couldn't get an upload URL — are you signed in as an admin?");
  const { uploadUrl, s3Key } = await res.json();
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    body: csv,
    headers: { 'Content-Type': 'text/csv', 'Content-Disposition': 'attachment' },
  });
  if (!put.ok) throw new Error(`Upload to storage failed (${put.status}).`);
  return s3Key;
};

const ImportXifinCoveragePopup = ({ onClose }) => {
  const [file, setFile] = useState(null);
  const [mrns, setMrns] = useState('');
  const [override, setOverride] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(null); // 'preview' | 'apply' | null
  const [data, setData] = useState(null);
  const { showAlert, AlertType } = useAlert();
  const announced = useRef(null);

  const status = data?.status || null;
  const isRunning = data ? !data.finished && RUNNING_STATUSES.has(status) : false;
  const isPreviewDone = status === 'previewed';
  const isApplied = status === 'applied';
  const isFailed = status === 'failed';
  const isBusy = submitting !== null || isRunning;

  const rows = useMemo(() => data?.results || [], [data]);
  const failures = useMemo(() => data?.failures || [], [data]);
  const progressPct =
    data?.totalPatients > 0 ? Math.round((data.processedPatients / data.totalPatients) * 100) : 0;

  const reset = () => {
    setData(null);
    setError('');
    announced.current = null;
  };

  const loadLatestPreview = useCallback(async () => {
    try {
      const response = await apiRequest({
        endpoint: LATEST_PREVIEW_ENDPOINT,
        method: HttpMethods.Get,
      });
      const type = response.headers.get('content-type') || '';
      if (!type.includes('application/json')) {
        const message = 'The XIFIN coverage import API is not available on this environment yet.';
        setError(message);
        showAlert(AlertType.Error, '', message);
        return false;
      }
      const body = response.ok ? await response.json() : null;
      if (body?.importId) {
        if (body.finished) announced.current = body.importId;
        setData(body);
        return true;
      }
      showAlert(AlertType.Success, '', "You're all caught up, nothing waiting for review.");
    } catch {
      const message = 'Could not reach the XIFIN coverage import API. Try again.';
      setError(message);
      showAlert(AlertType.Error, '', message);
    }
    return false;
  }, []);

  useEffect(() => {
    loadLatestPreview();
  }, [loadLatestPreview]);

  useEffect(() => {
    if (!data?.importId || data.finished) return undefined;
    let misses = 0;
    const timer = setInterval(async () => {
      try {
        const response = await apiRequest({
          endpoint: stateEndpoint(data.importId),
          method: HttpMethods.Get,
        });
        if (!response.ok) throw new Error('poll');
        setData(await response.json());
        misses = 0;
      } catch {
        misses += 1;
        if (misses >= 4) {
          setError('Lost contact with the import — reopen this window to check on it.');
        }
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [data?.importId, data?.finished]);

  useEffect(() => {
    if (!data?.finished || announced.current === data.importId) return;
    announced.current = data.importId;
    if (data.status === 'previewed') {
      showAlert(AlertType.Success, '', 'Preview ready — review before importing.');
    } else if (data.status === 'applied') {
      showAlert(AlertType.Success, '', 'XIFIN coverage import finished.');
    } else if (data.status === 'failed') {
      showAlert(AlertType.Error, '', 'XIFIN coverage import failed — see details.');
    }
  }, [data?.finished, data?.importId, data?.status, showAlert, AlertType]);

  const handleFileChange = (event) => {
    const selected = event.target.files[0] || null;
    reset();
    if (selected && !isCsvFile(selected)) {
      setFile(null);
      setError(FILE_ERROR);
      event.target.value = '';
      return;
    }
    setFile(selected);
  };

  const runPreview = useCallback(async () => {
    if (!file || isBusy) return;
    if (!isCsvFile(file)) {
      setError(FILE_ERROR);
      return;
    }

    setSubmitting('preview');
    setError('');
    announced.current = null;

    const mrnList = parseMrnList(mrns);

    try {
      const s3Key = await uploadToS3(await scopedCsv(file, mrnList));
      const response = await apiRequest({
        endpoint: PREVIEW_ENDPOINT,
        method: HttpMethods.Post,
        body: { s3Key, filename: file.name, mrns: mrnList.join(',') || null },
      });
      const responseBody = await response.json();
      if (!response.ok) {
        setError(getRestErrorMessage(responseBody) || DEFAULT_ERROR);
        return;
      }
      setData(responseBody);
    } catch (err) {
      setError(err?.message || DEFAULT_ERROR);
    } finally {
      setSubmitting(null);
    }
  }, [file, isBusy, mrns]);

  const applyPreview = useCallback(async () => {
    if (!data?.importId || isBusy || !isPreviewDone) return;

    setSubmitting('apply');
    setError('');
    announced.current = null;

    try {
      const response = await apiRequest({
        endpoint: applyEndpoint(data.importId),
        method: HttpMethods.Post,
        body: { override },
      });
      const responseBody = await response.json();
      if (!response.ok) {
        setError(getRestErrorMessage(responseBody) || DEFAULT_ERROR);
        if (response.status === 409) setOverride(true);
        return;
      }
      setData(responseBody);
    } catch {
      setError(DEFAULT_ERROR);
    } finally {
      setSubmitting(null);
    }
  }, [data?.importId, isBusy, isPreviewDone, override]);

  const headline = () => {
    if (isRunning) return status === 'previewing' ? 'Previewing…' : 'Importing…';
    if (isPreviewDone) return 'Preview';
    if (isApplied) return 'Import finished';
    if (isFailed) return 'Import failed';
    return '';
  };

  return (
    <BasePopup
      id="import-xifin-coverage"
      open
      onClose={isBusy ? undefined : onClose}
      title="Import XIFIN Coverage Updates"
      subtitle="Admin only"
      stackedHeader
    >
      <div className={styles.body}>
        <p className={styles.instructions}>
          Uploading the consolidated XIFIN ADT CSV rebuilds every listed patient&rsquo;s coverages
          and exemptions to match XIFIN 1:1 (matched by <strong>xifin_patient_id</strong> = MRN).
        </p>
        <p className={styles.instructions}>Expected columns: {EXPECTED_COLUMNS.join(', ')}.</p>

        {!data && submitting === null && (
          <button type="button" className={styles.resumeButton} onClick={loadLatestPreview}>
            Check for a saved or running preview
          </button>
        )}

        <label className={styles.fileLabel} htmlFor="xifin-coverage-file">
          <span>XIFIN coverage CSV</span>
          <input
            id="xifin-coverage-file"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isBusy}
          />
        </label>

        <label className={styles.fileLabel} htmlFor="xifin-coverage-mrns">
          <span>Limit to MRNs (optional, comma-separated)</span>
          <input
            id="xifin-coverage-mrns"
            type="text"
            placeholder="e.g. 10000, 10002, 3151"
            value={mrns}
            onChange={(event) => {
              reset();
              setMrns(event.target.value);
            }}
            disabled={isBusy}
          />
        </label>

        <Checkbox
          id="xifin-coverage-override"
          label="Allow older file (Use with caution)"
          description="Check this if you want to upload a file that isn't newer than your last import."
          checked={override}
          onChange={setOverride}
          disabled={isBusy}
        />

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        {isRunning && (
          <div className={styles.progress}>
            <div className={styles.progressBar}>
              <span style={{ width: `${progressPct}%` }} />
            </div>
            <span className={styles.progressText}>
              {data.processedPatients} / {data.totalPatients} patients
            </span>
          </div>
        )}

        {data && (
          <div className={styles.summary}>
            <div className={styles.summaryTitle}>
              {headline()}
              {data.filename ? ` — ${data.filename}` : ''}
              {data.scopeMrns ? ` · limited to ${data.scopeMrns.length} MRN(s)` : ''}
            </div>
            <dl className={styles.summaryGrid}>
              {COUNT_ROWS.map(({ key, label }) =>
                typeof data.counts?.[key] === 'number' && data.counts[key] > 0 ? (
                  <div key={key} className={styles.summaryItem}>
                    <dt>{label}</dt>
                    <dd>{data.counts[key]}</dd>
                  </div>
                ) : null,
              )}
            </dl>
            {data.missingScopedMrns?.length > 0 && (
              <p className={styles.warning}>Not in the file: {data.missingScopedMrns.join(', ')}</p>
            )}
            {isPreviewDone && (
              <p className={styles.warning}>
                This preview is saved — you can close this window and come back to apply it.
              </p>
            )}
            {isApplied && (
              <p className={styles.warning}>
                Import id <strong>{data.importId}</strong> — keep this to revert the run.
              </p>
            )}
          </div>
        )}

        {data && data.resultsTotal > data.resultsShown && (
          <p className={styles.warning}>
            {data.resultsShown > 0
              ? `Listing the ${data.resultsShown} patient(s) that need a look`
              : 'No patients need a look'}{' '}
            — {data.resultsTotal - data.resultsShown} more would be replaced cleanly (see counts
            above).
          </p>
        )}

        {rows.length > 0 && (
          <div className={styles.tableScroll}>
            <table className={styles.resultsTable}>
              <thead>
                <tr>
                  <th>MRN</th>
                  <th>Outcome</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.mrn}>
                    <td>{row.mrn}</td>
                    <td>
                      <span className={[styles.chip, outcomeClass(row.outcome)].join(' ')}>
                        {row.outcome.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className={styles.detailCell}>{row.reason || summaryLine(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {failures.length > 0 && (
          <div className={styles.tableScroll}>
            <table className={styles.resultsTable}>
              <thead>
                <tr>
                  <th>MRN</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {failures.map((f) => (
                  <tr key={f.mrn}>
                    <td>{f.mrn}</td>
                    <td className={styles.detailCell}>{f.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          id="xifin-coverage-preview"
          text={submitting === 'preview' || status === 'previewing' ? 'Previewing...' : 'Preview'}
          onClick={runPreview}
          disabled={!file || isBusy || data != null}
          isLoading={submitting === 'preview' || status === 'previewing'}
          width={160}
        />
        <Button
          id="xifin-coverage-import"
          text={
            submitting === 'apply' || status === 'processing' ? 'Importing...' : 'Import Changes'
          }
          onClick={applyPreview}
          disabled={isBusy || !isPreviewDone}
          isLoading={submitting === 'apply' || status === 'processing'}
          width={180}
        />
      </div>
    </BasePopup>
  );
};

ImportXifinCoveragePopup.propTypes = {
  onClose: PropTypes.func,
};

export default ImportXifinCoveragePopup;
